import { Grid } from '@mui/material';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, DateInput, SelectInput } from 'sharedComponents/inputGroup';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { RootState } from 'store/store';
import PreApprovedHeader from './header/PreApprovedHeader';
import { get } from 'lodash';
import { RESIDENTIAL_OPTION } from '../DigitalRetailingConstant';

const Step9: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const { control, trigger, setValue, getValues, watch } = useFormContext();

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const hasCalledNextStep = React.useRef(false);

  const pre_approved_ress_statusWatcher = watch('pre_approved_ress_status');

  useEffect(() => {
    const { firstName, lastName, email, phone } = getValues();
    setValue('pre_approved_firstname', firstName);
    setValue('pre_approved_lastname', lastName);
    setValue('pre_approved_emailAddress', email);
    setValue('pre_approved_phonenumber', phone);
  }, []);

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    onNext?.();
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <PreApprovedHeader currentStep={0} />

      <div className='dr-widget-steps pre-approved-steps'>
        <div className='dr-widget-steps__content'>
          <h2 className='dr-widget-steps__title'>{t('Get pre approved now ')}</h2>
          <p className='dr-widget-steps__step-note text-secondary'>
            {t('Complete these applications to save time at the dealership')}
          </p>
          <div className='dr-widget-steps__form-wrapper'>
            <h3 className='form-section-title'>Personal Information</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_firstname'
                  control={control}
                  label={t('stepperForm.firstName')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_middlename'
                  control={control}
                  label={t('digitalRetailing.middleName')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_lastname'
                  control={control}
                  label={t('stepperForm.lastName')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <DateInput
                  name='pre_approved_dob'
                  control={control}
                  rules={formRequiredRules(errorMessage(t).required)}
                  label={t('stepperForm.DOB')}
                  triggerValidationForField={trigger}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonPhoneInput
                  name='pre_approved_phonenumber'
                  control={control}
                  label={t('stepperForm.phone')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={{
                    ...formRequiredRules(errorMessage(t).required),
                    ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
                  }}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_emailAddress'
                  control={control}
                  label={t('stepperForm.emailEg')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={{
                    ...formRequiredRules(errorMessage(t).required),
                    ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
                  }}
                />
              </Grid>
            </Grid>
            <h3 className='form-section-title'>Residential Address</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item md={8} sm={8} xs={12} marginBottom={3}>
                <AutoCompleteAddress
                  name='pre_approved_address'
                  control={control}
                  setValue={setValue}
                  label={t('stepperForm.address')}
                  placeholder={t('stepperForm.enterAddress')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  triggerValidationForField={trigger}
                />
              </Grid>
              <Grid item md={4} sm={4} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_years_at_address'
                  control={control}
                  label={t('digitalRetailing.yearsAtAddress')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  type='number'
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
            </Grid>
            <h3 className='form-section-title'>{t('digitalRetailing.residentialInformation')}</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <SelectInput
                  name='pre_approved_ress_status'
                  control={control}
                  label={t('digitalRetailing.residentialStatus')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  options={RESIDENTIAL_OPTION(t)}
                  triggerValidationForField={trigger}
                  defaultValue={pre_approved_ress_statusWatcher}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12}>
                <CommonInput
                  name='pre_approved_monthly_payment'
                  control={control}
                  label={t('digitalRetailing.monthlyResidentialPayment')}
                  triggerValidationForField={trigger}
                  type='number'
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </>
  );
};

const StepperForm9 = React.forwardRef(Step9);

export default StepperForm9;
